import {
  IUseCase,
  GetRideHistoryUseCaseRequest,
  GetDashboardDataUseCaseRequest,
  GetDashboardDataUseCaseResponse,
} from './interfaces'
import { IRideRepository } from '@/core/domain/repositories/IRideRepository'
import { IDriverRepository } from '@/core/domain/repositories/IDriverRepository'
import { RideAggregates } from '@/core/domain/entities/Ride'
import { Result, createOk, createErr, isOk } from '@/shared/utils/Result'
import { ValidationError } from '@/shared/errors'
import { DateRangeString } from '@/shared/types'

export class GetRideHistoryUseCase
  implements IUseCase<GetRideHistoryUseCaseRequest, RideAggregates[]>
{
  constructor(private readonly rideRepository: IRideRepository) {}

  async execute(
    request: GetRideHistoryUseCaseRequest
  ): Promise<Result<RideAggregates[], Error>> {
    try {
      // Validate input
      if (!request.userId || request.userId.trim() === '') {
        return createErr(new ValidationError('User ID is required'))
      }

      // Build date range if provided
      const dateRange: DateRangeString | undefined =
        request.dateStart && request.dateEnd
          ? { start: request.dateStart, end: request.dateEnd }
          : undefined

      // Get rides from repository
      const ridesResult = await this.rideRepository.getRideAggregates(
        request.userId,
        dateRange
      )

      if (!isOk(ridesResult)) {
        return createErr(ridesResult.error)
      }

      let rides = Array.isArray(ridesResult.value)
        ? ridesResult.value
        : [ridesResult.value]

      return createOk(rides)
    } catch (error) {
      console.error('GetRideHistoryUseCase failed:', error)
      return createErr(
        error instanceof Error ? error : new Error('Failed to get ride history')
      )
    }
  }
}

export class GetDashboardDataUseCase
  implements
    IUseCase<GetDashboardDataUseCaseRequest, GetDashboardDataUseCaseResponse>
{
  constructor(
    private readonly rideRepository: IRideRepository,
    private readonly driverRepository: IDriverRepository
  ) {}

  async execute(
    request: GetDashboardDataUseCaseRequest
  ): Promise<Result<GetDashboardDataUseCaseResponse, Error>> {
    try {
      // Validate input
      if (!request.userId || request.userId.trim() === '') {
        return createErr(new ValidationError('User ID is required'))
      }

      // Build date range if provided
      const dateRange: DateRangeString | undefined =
        request.dateStart && request.dateEnd
          ? { start: request.dateStart, end: request.dateEnd }
          : undefined

      const [driverAggregatesResult, rideAggregatesResult] = await Promise.all([
        this.driverRepository.getDriverAggregates(request.userId),
        this.rideRepository.getRideAggregates(request.userId, dateRange),
      ])

      if (!isOk(driverAggregatesResult)) {
        return createErr(driverAggregatesResult.error)
      }

      if (!isOk(rideAggregatesResult)) {
        return createErr(rideAggregatesResult.error)
      }

      return createOk({
        driverAggregates: driverAggregatesResult.value,
        rideAggregates: rideAggregatesResult.value,
      })
    } catch (error) {
      console.error('GetDashboardDataUseCase failed:', error)
      return createErr(
        error instanceof Error
          ? error
          : new Error('Failed to get dashboard data')
      )
    }
  }
}
